import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { tokenStorage } from '../utils/tokenStorage';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: false,
  user: null,
  token: tokenStorage.getTokenSync(), // Get token synchronously for initial state (web only)
  loading: true,
};

// Cloud Function URLs
const CLOUD_FUNCTIONS_BASE = 'https://us-central1-doc-widget-tracker-prod.cloudfunctions.net';

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [debugLog, setDebugLog] = useState('');
  const queryClient = useQueryClient();
  const isDev = process.env.NODE_ENV === 'development';

  // Helper function to add debug logs
  const addDebugLog = (message, data = null) => {
    if (!isDev) return;
    const timestamp = new Date().toISOString();
    const logEntry = `[${timestamp}] ${message}${data ? '\n' + JSON.stringify(data, null, 2) : ''}\n\n`;
    console.log(logEntry);
    setDebugLog(prev => prev + logEntry);
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const reason = localStorage.getItem('auth_logout_reason');
      if (reason) {
        toast.info(reason);
        localStorage.removeItem('auth_logout_reason');
      }
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = await tokenStorage.getToken();
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get('/auth/me');
          if (response.data && response.data.user) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: response.data.user,
                token: token,
              },
            });
          } else {
            await tokenStorage.removeToken();
            delete api.defaults.headers.common['Authorization'];
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          await tokenStorage.removeToken();
          delete api.defaults.headers.common['Authorization'];
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  const loginMutation = useMutation(
    async ({ phone, otp }) => {
      const deviceId = await tokenStorage.getOrCreateDeviceId();
      // Always use rememberMe=true for persistent login
      const res = await api.post('/auth/login-phone', { phone, otp, rememberMe: true, deviceId });
      return res;
    },
    {
      onSuccess: async (response) => {
        const { token, user } = response.data;
        try {
          // Always store token persistently (web: localStorage, mobile: Capacitor Preferences)
          await tokenStorage.setToken(token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (e) {
          console.error('Error storing token:', e);
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });
        toast.success('Login successful!');
      },
      onError: (error) => {
        const message = error?.response?.data?.message || error?.message || 'Login failed';
        toast.error(message);
      },
    }
  );

  // UPDATED: Send OTP using direct fetch to Cloud Function
  const sendOTPMutation = useMutation(
    async (phone) => {
      if (isDev) setDebugLog(''); // Clear previous logs (dev only)
      
      const cloudFunctionUrl = `${CLOUD_FUNCTIONS_BASE}/sendOtpPhone`;
      
      // Ensure phone is a clean string
      const cleanPhone = String(phone).replace(/\D/g, '').slice(-10);
      
      /*addDebugLog('🚀 Sending OTP Request', {
        url: cloudFunctionUrl,
        phone: cleanPhone,
        phoneType: typeof cleanPhone,
        originalPhone: phone
      });*/

      try {
        // Use native fetch to avoid any axios transformations
        const response = await fetch(cloudFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ phone: cleanPhone })
        });
        
        /*addDebugLog('📡 Response Headers', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          contentType: response.headers.get('content-type')
        });*/
        
        const responseText = await response.text();
        /*addDebugLog('📦 Raw Response Body', { responseText });*/
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          addDebugLog('⚠️ Failed to parse JSON', { error: e.message, responseText });
          data = { message: responseText };
        }
        
        /*addDebugLog('📦 Parsed Data', data);*/
        
        if (!response.ok) {
          const errorMsg = data.message || data.error || `HTTP ${response.status}: ${responseText}`;
          addDebugLog('❌ Request Failed', { errorMsg, status: response.status });
          throw new Error(errorMsg);
        }
        
        /*addDebugLog('✅ OTP Sent Successfully');*/
        return { data };
        
      } catch (error) {
        const errorDetails = {
          name: error.name,
          message: error.message,
          stack: error.stack?.split('\n').slice(0, 5).join('\n')
        };

        addDebugLog('❌ Exception Caught', errorDetails);
        
        // Avoid blocking alert popups in production (better UX + performance)
        if (isDev) {
          alert(`❌ OTP SEND FAILED\n\nError: ${error.message}\n\nPhone: ${cleanPhone}\n\nSee console for full logs`);
        }
        
        throw error;
      }
    },
    {
      onError: (error) => {
        const message = error?.message || 'Failed to send OTP';
        toast.error(message);
        addDebugLog('📱 Error toast shown', { message });
      },
    }
  );

  const registerMutation = useMutation(
    async ({ phone, otp, emailOtp, name, email, dateOfBirth, address, city, referralCode }) => {
      const deviceId = await tokenStorage.getOrCreateDeviceId();
      // Always use rememberMe=true for persistent login
      const res = await api.post('/auth/register-phone', {
        phone,
        otp,
        emailOtp,
        name,
        email,
        dateOfBirth,
        address,
        city,
        referralCode,
        rememberMe: true,
        deviceId
      });
      return res;
    },
    {
      onSuccess: async (response) => {
        const { token, user } = response.data;
        try {
          // Always store token persistently (web: localStorage, mobile: Capacitor Preferences)
          await tokenStorage.setToken(token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (e) {
          console.error('Error storing token:', e);
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });
        toast.success('Welcome to DrPay! Your account is ready.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Registration failed');
      },
    }
  );

  const verifyEmailMutation = useMutation(
    (token) => api.post('/auth/verify-email', { token }),
    {
      onSuccess: () => {
        toast.success('Email verified successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Email verification failed');
      },
    }
  );

  // Email OTP flow (used for mandating email + gating dashboard)
  const sendEmailOtpMutation = useMutation(
    ({ email }) => api.post('/auth/send-email-otp', { email }),
    {
      onSuccess: () => {
        toast.success('Verification code sent to your email');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || error?.message || 'Failed to send verification code');
      },
    }
  );

  const verifyEmailOtpMutation = useMutation(
    ({ email, otp }) => api.post('/auth/verify-email-otp', { email, otp }),
    {
      onSuccess: () => {
        toast.success('Email verified successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || error?.message || 'Email verification failed');
      },
    }
  );

  const logout = async () => {
    try {
      await tokenStorage.removeToken();
      delete api.defaults.headers.common['Authorization'];
      dispatch({ type: 'LOGOUT' });
      queryClient.clear();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const updateUser = (userData) => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
  };

  const refreshUser = async () => {
    try {
      const token = await tokenStorage.getToken();
      if (!token) return null;
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/auth/me');
      if (response.data?.user) {
        dispatch({ type: 'UPDATE_USER', payload: response.data.user });
        return response.data.user;
      }
      return null;
    } catch (e) {
      console.error('refreshUser failed:', e);
      return null;
    }
  };

  // Wrapper for login to support callbacks
  const login = (data, callbacks) => {
    loginMutation.mutate(data, callbacks);
  };

  const value = {
    ...state,
    login,
    sendOTP: sendOTPMutation.mutate,
    sendOTPAsync: sendOTPMutation.mutateAsync,
    register: registerMutation.mutate,
    refreshUser,
    verifyEmail: verifyEmailMutation.mutate,
    sendEmailOtp: sendEmailOtpMutation.mutate,
    verifyEmailOtp: verifyEmailOtpMutation.mutate,
    logout,
    updateUser,
    isLoggingIn: loginMutation.isLoading,
    isSendingOTP: sendOTPMutation.isLoading,
    isRegistering: registerMutation.isLoading,
    isVerifyingEmail: verifyEmailMutation.isLoading,
    isSendingEmailOtp: sendEmailOtpMutation.isLoading,
    isVerifyingEmailOtp: verifyEmailOtpMutation.isLoading,
    debugLog, // Expose debug logs
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};